import { createH3dPrototypeDataFromGltf } from '../3d/h3dGltfCompiler.js'
import { loadGltfFromArrayBuffer } from '../3d/h3dGeometryProbe.js'
import { optimizeModel } from '../3d/h3dOptimizer.js'
import { subdivideForLighting } from '../3d/h3dLightingSubdivision.js'
import { lightingSubdivisionOptionsForModel } from '../3d/h3dLightingQuality.js'
import { writeH3dPrototype } from '../3d/h3dPrototype.js'
import { useModel3dStore } from '../stores/model3dStore.js'
import { isResourceUsed } from '../utils/resourceUsage.js'
import { bytesToBase64 } from './httpTransfer.js'

export function h3dRuntimeFilename(model, used = new Set()) {
  const source = model?.name || model?.originalFilename || model?.filename || model?.uuid || 'model'
  const withoutExt = source.replace(/\.[^.]+$/i, '')
  const base = slugFilename(withoutExt) || 'model'
  let filename = `${base}.h3d0`
  if (!used.has(filename)) return filename

  const suffix = model?.uuid ? `-${String(model.uuid).slice(0, 8)}` : ''
  filename = `${base}${suffix}.h3d0`
  let counter = 2
  while (used.has(filename)) {
    filename = `${base}${suffix}-${counter}.h3d0`
    counter += 1
  }
  return filename
}

export function collectUsedModel3dResources(layer, store = useModel3dStore()) {
  return store.all.filter(model => isResourceUsed(layer, 'resource', model.uuid))
}

export function resolveUsedH3dRuntimeFilenames(layer, store = useModel3dStore()) {
  return resolveH3dRuntimeFilenames(collectUsedModel3dResources(layer, store))
}

export async function compileModel3dResource(model, store = useModel3dStore(), options = {}) {
  return (await compileModel3dResourceDetailed(model, store, options)).content
}

/**
 * Same compile, plus the numbers the resource panel reports. Callers that only want the
 * bytes use compileModel3dResource().
 *
 * `options.lightingSubdivision` wins when given; otherwise the resource's own
 * `lightingQuality` decides, so export, file transfer and preview all agree without each having
 * to know about the setting.
 */
export async function compileModel3dResourceDetailed(model, store = useModel3dStore(), options = {}) {
  if (!model) throw new Error('3D model resource not found')
  if (model.type && model.type !== 'glb') throw new Error(`3D model "${model.name}" is not a GLB resource`)
  if (model.storageId == null) throw new Error(`3D model "${model.name}" has no imported GLB file`)

  const source = await store.getModelArrayBuffer(model.uuid)
  if (!source) throw new Error(`GLB file for 3D model "${model.name}" is missing from storage`)

  try {
    const arrayBuffer = normalizeArrayBuffer(source)
    const gltf = await loadGltfFromArrayBuffer(arrayBuffer, model.originalFilename || model.filename || 'model.glb')
    const prototype = createH3dPrototypeDataFromGltf(gltf)
    const { model: optimized } = await optimizeModel(prototype, options.optimize)
    // After simplification, never before: the optimizer would only collapse the extra
    // triangles again.
    const lightingSubdivision = options.lightingSubdivision ?? lightingSubdivisionOptionsForModel(model)
    const { model: subdivided } = subdivideForLighting(optimized, lightingSubdivision)
    const content = writeH3dPrototype(subdivided)
    return {
      content,
      sourceTriangles: prototype.indices.length / 3,
      compiledTriangles: subdivided.indices.length / 3,
      compiledVertices: subdivided.positions.length / 3,
      byteLength: content.byteLength,
      lightingSubdivision,
    }
  } catch (error) {
    throw new Error(`Failed to compile 3D model "${model.name || model.filename || model.uuid}" to H3D0: ${error?.message || error}`)
  }
}

export async function buildUsedH3dResourceArtifacts(layer, store = useModel3dStore()) {
  const artifacts = []
  const cache = new Map()
  const filenames = resolveUsedH3dRuntimeFilenames(layer, store)

  for (const model of collectUsedModel3dResources(layer, store)) {
    if (cache.has(model.uuid)) continue
    const filename = filenames.get(model.uuid)
    const content = await compileModel3dResource(model, store)
    const artifact = {
      resourceType: 'model3d',
      uuid: model.uuid,
      name: model.name,
      sourceFilename: model.filename,
      filename,
      content,
      bytes: new Uint8Array(content),
      base64data: bytesToBase64(new Uint8Array(content)),
      mime: 'application/octet-stream',
    }
    cache.set(model.uuid, artifact)
    artifacts.push(artifact)
  }

  return artifacts
}

function resolveH3dRuntimeFilenames(models) {
  const usedNames = new Set()
  const filenames = new Map()
  for (const model of models) {
    const filename = h3dRuntimeFilename(model, usedNames)
    usedNames.add(filename)
    filenames.set(model.uuid, filename)
  }
  return filenames
}

function slugFilename(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function normalizeArrayBuffer(value) {
  if (value instanceof ArrayBuffer) return value
  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)
  }
  throw new Error('GLB source is not binary data')
}
